import { call, ForkEffect, put, takeEvery, select } from 'redux-saga/effects';
import { combineReducers } from 'redux';
import {
  Action,
  ActionCreator,
  Success,
  actionCreatorFactory,
  ActionCreatorFactory,
} from 'typescript-fsa';
import {
  ReducerBuilder,
  reducerWithInitialState,
} from 'typescript-fsa-reducers';
import { produce } from 'immer';

import { API } from './types';
import apiCaller from './ApiCaller';
import refreshTokenIfNeeded from 'services/Api/checkIfTokenExpired';
import { traceAssessmentRequest } from '../dragDiagnostics';

type ConstructorProps = {
  socketUrl?: string;
  apiUrl?: string;
  selectors?: { varName: string; selector: (state: any) => any }[];
  tokenSelector?: (state: any) => any;
  moduleName: string;
  initialState: Record<string, unknown>;
};

const errorText = (error: unknown) => {
  if (error === null || error === undefined) return 'Request failed.';
  try {
    return String(error);
  } catch {
    return 'Request failed.';
  }
};

export default class StateManager {
  public reducer: ReducerBuilder<any>;
  public effects: ForkEffect[] = [];
  public socketEvents: {
    [key: string]: ActionCreator<any>;
  };
  readonly socketUrl: string;
  readonly apiUrl: string;

  private selectors: {
    varName: string;
    selector: (state: any) => any;
  }[];
  private tokenSelector: (state: any) => any;

  public moduleName: string;
  public actionCreator: ActionCreatorFactory;

  constructor(props: ConstructorProps) {
    this.socketUrl = props.socketUrl || '';
    this.apiUrl = props.apiUrl || '';

    this.selectors = props.selectors || [];
    this.tokenSelector =
      props.tokenSelector ||
      (() => {
        /*empty*/
      });

    this.moduleName = props.moduleName;
    this.reducer = reducerWithInitialState(props.initialState);
    this.socketEvents = {};

    this.actionCreator = actionCreatorFactory(this.moduleName);
  }

  public createLocalEvent(
    actionName: string,
    reducerFn: (state: any, payload: any) => void
  ) {
    const action = this.actionCreator<any>(actionName);

    this.reducer.case(action, (state, payload) =>
      produce(state, (draft: any) => {
        reducerFn(draft, payload);
      })
    );

    return action;
  }

  public createApi<Payload, Result, ApiState>(
    actionName: string,
    api: API<Payload, Result, ApiState>
  ): (payload: Payload) => Action<Payload> {
    const self = this;
    const asyncAction = this.actionCreator.async<Payload, Result, Error>(
      actionName
    );
    const { reducer, selectors } = this;

    this.effects.push(
      // when this action is dispatched
      takeEvery(asyncAction.started, function* (action: Action<Payload>) {
        let dragRequest: ReturnType<typeof traceAssessmentRequest>;
        // console.log('doing something');
        try {
          // fetch auth
          // const token = yield select(self.tokenSelector);
          // const token = yield call(refreshTokenIfNeeded);

          const additionalVars: { [key: string]: any } = {};
          for (const item of self.selectors) {
            const { varName, selector } = item;
            additionalVars[varName] = yield select(selector);
          }
          for (const item of selectors) {
            const { varName, selector } = item;
            additionalVars[varName] = yield select(selector);
          }

          const payloadAny: any = action.payload as any;
          let dataToSend: FormData | Record<string, any>;
          let isForm = false;

          if (payloadAny instanceof FormData) {
            // If the caller passed FormData, inject additionalVars into it
            isForm = true;
            dataToSend = payloadAny as FormData;

            // Append each additional var into the same FormData
            Object.entries(additionalVars).forEach(([k, v]) => {
              if (v !== undefined && v !== null) {
                // convert to string if not already
                (dataToSend as FormData).append(k, String(v));
              }
            });
          } else {
            // Otherwise, just merge into a plain JS object
            dataToSend = { ...additionalVars, ...(payloadAny || {}) };
          }

          // call api
          dragRequest = traceAssessmentRequest(actionName, dataToSend);
          const { result, status } = yield call(() =>
            apiCaller<Payload>({
              ...api,
              data: dataToSend as any,
              // token,
              apiUrl: self.apiUrl,
            })
          );

          dragRequest?.response(status);
          if (String(status)[0] !== '2') {
            // console.log(result);
            return yield put(
              asyncAction.failed({ params: action.payload, error: result })
            );
          }

          yield put(asyncAction.done({ params: action.payload, result }));
        } catch (error) {
          dragRequest?.failed(error);
          // console.log(error);
          yield put(
            asyncAction.failed({
              params: action.payload,
              error:
                error instanceof Error ? error : new Error(errorText(error)),
            })
          );
        }
      })
    );

    reducer.case(asyncAction.started, (state, payload) =>
      produce(state, (draft: any) => {
        draft.waiting = true;
        draft.error = undefined;
        if (api.startReducer) api.startReducer(draft, payload);
      })
    );

    reducer.case(asyncAction.failed, (state, { params, error }) =>
      produce(state, (draft: any) => {
        draft.waiting = false;
        draft.lastAction = actionName;
        draft.error = errorText(error);
        if (api.failReducer) api.failReducer(draft, error, params as Payload);
      })
    );

    reducer.case(asyncAction.done, (state, { params, result }) =>
      produce(state, (draft: any) => {
        draft.waiting = false;
        draft.lastAction = actionName;
        api.successReducer(draft, result as Result, params as Payload);
      })
    );

    return (payload: Payload) => asyncAction.started(payload);
  }

  public createSocketListener<Result, ApiState>(
    type: string,
    onReceive: (state: ApiState, result: Result) => void
  ) {
    const action = this.actionCreator<Success<unknown, Result>>(type);
    this.socketEvents[type] = action;

    this.reducer.case(action, (state, payload: any) =>
      produce(state, (draft: any) => {
        onReceive(draft, payload as Result);
      })
    );
  }
}
